/**
 * SSE event id 解析与排序。
 *
 * 唯一格式（ADR-client-001 W2 已闭环）：`{runId}:seq:{sequence}`（persistent）。
 * server 侧 `RunEventCursor` 只编码/接受该格式；legacy `{runId}:{timestamp}-{seq}`
 * 分支已于 2026-09-04 删除（server 拒收 legacy 续传且前端 id 纯内存，分支不可达）。
 */
export type OrderedSseEventId = { format: 'persistent'; runId: string; sequence: string };

export function compareUnsignedDecimal(left: string, right: string): number {
  const normalizedLeft = left.replace(/^0+/, '') || '0';
  const normalizedRight = right.replace(/^0+/, '') || '0';
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length < normalizedRight.length ? -1 : 1;
  }
  return normalizedLeft.localeCompare(normalizedRight);
}

export function incrementUnsignedDecimal(value: string): string {
  const digits = (value.replace(/^0+/, '') || '0').split('');
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    const next = Number(digits[index]) + 1;
    if (next < 10) {
      digits[index] = String(next);
      return digits.join('');
    }
    digits[index] = '0';
  }
  return `1${digits.join('')}`;
}

export function parseOrderedSseEventId(eventId: string): OrderedSseEventId | null {
  const marker = ':seq:';
  const markerIndex = eventId.lastIndexOf(marker);
  if (markerIndex <= 0) return null;
  const runId = eventId.slice(0, markerIndex);
  const sequence = eventId.slice(markerIndex + marker.length);
  if (!runId || !/^\d+$/.test(sequence)) return null;
  return { format: 'persistent', runId, sequence };
}

export function isDuplicateOrOlderSseEvent(
  currentEventId: string | null,
  nextEventId: string
): boolean {
  if (!currentEventId) return false;
  if (currentEventId === nextEventId) return true;
  const current = parseOrderedSseEventId(currentEventId);
  const next = parseOrderedSseEventId(nextEventId);
  if (!current || !next || current.runId !== next.runId) {
    return false;
  }
  return compareUnsignedDecimal(next.sequence, current.sequence) <= 0;
}
